import { useRef, useState } from "react";

import clsx from "clsx";
import { AnimatePresence, motion } from "framer-motion";

import { type QuickSelectOption } from "@features/metrics-explorer/types";
import {
  autoUpdate,
  flip,
  FloatingPortal,
  offset,
  shift,
  size,
  useClick,
  useDismiss,
  useFloating,
  useInteractions,
  useListNavigation,
} from "@floating-ui/react";
import { renderChevronAnimation } from "@shared/selects/utils";

import { QuickSelectButton } from "../quick-select/quick-select-button";
import { useQuickSelectOptions } from "../quick-select/use-quick-select-options";

interface Props {
  width: number;
  onAddTile: () => void;
  onQuickSelect: (value: QuickSelectOption) => void;
}

export const AddMetricButton = ({ width, onAddTile, onQuickSelect }: Props) => {
  const quickSelectOptions = useQuickSelectOptions();

  const [isOpen, setIsOpen] = useState(false);
  const [activeIndex, setActiveIndex] = useState<number | null>(null);

  const listRef = useRef<(HTMLButtonElement | null)[]>([]);

  const { refs, floatingStyles, context } = useFloating({
    open: isOpen,
    onOpenChange: setIsOpen,
    transform: false,
    placement: "bottom",
    whileElementsMounted: autoUpdate,
    middleware: [
      flip(),
      shift(),
      offset(1),
      size({
        padding: 10,
        apply({ elements, availableHeight }) {
          elements.floating.style.maxHeight = `${availableHeight}px`;
        },
      }),
    ],
  });

  const click = useClick(context);
  const dismiss = useDismiss(context);

  const listNavigation = useListNavigation(context, {
    listRef,
    activeIndex,
    virtual: true,
    focusItemOnOpen: true,
    onNavigate: setActiveIndex,
  });

  const { getReferenceProps, getFloatingProps, getItemProps } = useInteractions(
    [click, dismiss, listNavigation],
  );

  return (
    <div
      className={clsx(
        "h-full border border-v2-secondary rounded-lg bg-v2-secondary",
      )}
      style={{ width: isNaN(width) ? 0 : width }}
    >
      <div className="border-b border-v2-secondary mx-6 pt-4 pb-4">
        <button
          className="flex items-center gap-3 text-sm group"
          onClick={onAddTile}
        >
          <i
            className={clsx(
              "fa-sharp fa-regular fa-plus w-[45px] h-[45px] bg-v2-highlight-information text-v2-action",
              "shrink-0 flex items-center justify-center rounded-full text-xl",
              "group-hover:text-white group-hover:bg-cosmic",
            )}
          ></i>
          <span className="font-bold group-hover:text-v2-action">
            ADD METRIC
          </span>
        </button>
      </div>
      <div>
        <button
          className={clsx(
            "px-6 pt-4 pb-[18px] block w-full font-bold text-sm hover:text-v2-action",
            "flex items-center justify-between",
            { "text-v2-action": isOpen },
          )}
          ref={refs.setReference}
          {...getReferenceProps({
            onKeyDown(e) {
              if (e.code === "Enter" && activeIndex !== null) {
                e.preventDefault();

                setIsOpen(false);

                const option = quickSelectOptions[activeIndex];

                if (option) {
                  onQuickSelect(option);
                }
              }
            },
          })}
        >
          <span>QUICK SELECT</span>
          {renderChevronAnimation(isOpen)}
        </button>
        <FloatingPortal>
          <AnimatePresence>
            {isOpen && (
              <motion.div
                transition={{ ease: "easeOut", duration: 0.1 }}
                initial={{ opacity: 0, translateY: "0.25rem" }}
                animate={{ opacity: 1, translateY: 0 }}
                exit={{ opacity: 0, translateY: "0.25rem" }}
                ref={refs.setFloating}
                style={{
                  ...floatingStyles,
                  minWidth: isNaN(width) ? 0 : width,
                }}
                className={clsx(
                  "z-[2005] rounded-lg border border-v2-secondary bg-v2-secondary p-4",
                  "text-sm font-sans 1max-h-[300px] overflow-y-auto",
                )}
                {...getFloatingProps()}
              >
                <div className="space-y-2 font-normal">
                  {quickSelectOptions.map((option, index) => {
                    return (
                      <QuickSelectButton
                        key={index}
                        ref={(node) => {
                          listRef.current[index] = node;
                        }}
                        title={option.title}
                        hasFocus={activeIndex === index}
                        getItemProps={getItemProps}
                        onSelect={() => {
                          setIsOpen(false);
                          onQuickSelect(option);
                        }}
                      />
                    );
                  })}
                </div>
              </motion.div>
            )}
          </AnimatePresence>
        </FloatingPortal>
      </div>
    </div>
  );
};
